import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function CreatorAnalyticsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [stats, setStats] = useState({
    totalCreations: 0,
    totalVideos: 0,
    totalMusic: 0,
    totalGames: 0,
    totalViews: 0,
    followersCount: 0,
  });
  const [creationsPerDay, setCreationsPerDay] = useState([]);
  const [viewsPerDay, setViewsPerDay] = useState([]);
  const [followerGrowth, setFollowerGrowth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAnalytics = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch generated_assets
      const { data: assets, error: assetsErr } = await supabase?.from('generated_assets')?.select('id, asset_type, views, created_at')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (assetsErr && assetsErr?.code !== 'PGRST116') throw assetsErr;

      // Fetch generated_music
      const { data: music, error: musicErr } = await supabase?.from('generated_music')?.select('id, created_at')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (musicErr && musicErr?.code !== 'PGRST116') throw musicErr;

      // Fetch creator profile for followers
      const { data: creator } = await supabase?.from('creators')?.select('followers_count')?.eq('user_id', user?.id)?.maybeSingle();

      // Fetch follower growth (last 7 days)
      const { data: followers } = await supabase?.from('creator_followers')?.select('created_at')?.eq('creator_id', creator?.id || '00000000-0000-0000-0000-000000000000')?.order('created_at', { ascending: true });

      const assetList = assets || [];
      const musicList = music || [];

      const totalVideos = assetList?.filter((a) => a?.asset_type === 'video')?.length;
      const totalGames = assetList?.filter((a) => a?.asset_type === 'game')?.length;
      const totalViews = assetList?.reduce((sum, a) => sum + (a?.views || 0), 0);

      setStats({
        totalCreations: assetList?.length + musicList?.length,
        totalVideos,
        totalMusic: musicList?.length,
        totalGames,
        totalViews,
        followersCount: creator?.followers_count || 0,
      });

      // Build creations per day (last 7 days)
      const days = buildLast7Days();
      const creationsMap = {};
      const viewsMap = {};
      days?.forEach((d) => { creationsMap[d] = 0; viewsMap[d] = 0; });

      assetList?.forEach((a) => {
        const day = new Date(a.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (creationsMap?.[day] !== undefined) creationsMap[day]++;
        if (viewsMap?.[day] !== undefined) viewsMap[day] += (a?.views || 0);
      });
      musicList?.forEach((m) => {
        const day = new Date(m.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (creationsMap?.[day] !== undefined) creationsMap[day]++;
      });

      setCreationsPerDay(days?.map((d) => ({ day: d, count: creationsMap?.[d] })));
      setViewsPerDay(days?.map((d) => ({ day: d, views: viewsMap?.[d] })));

      // Follower growth per day
      const followerMap = {};
      days?.forEach((d) => { followerMap[d] = 0; });
      (followers || [])?.forEach((f) => {
        const day = new Date(f.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (followerMap?.[day] !== undefined) followerMap[day]++;
      });
      setFollowerGrowth(days?.map((d) => ({ day: d, followers: followerMap?.[d] })));
    } catch (err) {
      console.error('[CreatorAnalytics]', err?.message);
      setError(err?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/home-landing-screen'); return; }
    loadAnalytics();
  }, [isAuthenticated, loadAnalytics, navigate]);

  const STAT_CARDS = [
    { label: 'Total Creations', value: stats?.totalCreations, icon: 'Layers', color: '#6366F1' },
    { label: 'Video Generations', value: stats?.totalVideos, icon: 'Video', color: '#8B5CF6' },
    { label: 'Music Generations', value: stats?.totalMusic, icon: 'Music', color: '#EC4899' },
    { label: 'Game Generations', value: stats?.totalGames, icon: 'Gamepad2', color: '#10B981' },
    { label: 'Total Views', value: stats?.totalViews?.toLocaleString(), icon: 'Eye', color: '#F59E0B' },
    { label: 'Followers', value: stats?.followersCount, icon: 'Users', color: '#06B6D4' },
  ];

  const tooltipStyle = { background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, color: 'var(--color-foreground)', fontSize: 12 };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/creator-dashboard')} className="p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
              <Icon name="BarChart3" size={16} color="white" />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Creator Analytics</h1>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Your creation performance</p>
            </div>
          </div>
          <button onClick={loadAnalytics} className="ml-auto p-2 rounded-lg hover:bg-[var(--color-muted)] transition-colors" title="Refresh">
            <Icon name="RefreshCw" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
        {error && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>{error}</div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAT_CARDS?.map((s) => (
            <div key={s?.label} className="rounded-2xl p-4 flex flex-col items-center gap-2 text-center" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
              {loading ? (
                <div className="w-full h-12 rounded-lg animate-pulse" style={{ background: 'var(--color-muted)' }} />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${s?.color}18` }}>
                    <Icon name={s?.icon} size={16} color={s?.color} />
                  </div>
                  <p className="text-xl font-bold" style={{ color: s?.color, fontFamily: 'var(--font-data)' }}>{s?.value}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{s?.label}</p>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Creations per day */}
          <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Creations per Day (Last 7 Days)</h3>
            {loading ? (
              <div className="h-40 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={creationsPerDay} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" fill="#6366F1" radius={[4, 4, 0, 0]} name="Creations" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Views per day */}
          <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Views per Day (Last 7 Days)</h3>
            {loading ? (
              <div className="h-40 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={viewsPerDay} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="views" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Views" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Follower Growth */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>New Followers (Last 7 Days)</h3>
          {loading ? (
            <div className="h-40 rounded-xl animate-pulse" style={{ background: 'var(--color-muted)' }} />
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={followerGrowth} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="followers" stroke="#06B6D4" strokeWidth={2} dot={{ r: 4, fill: '#06B6D4' }} name="New Followers" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function buildLast7Days() {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d?.setDate(d?.getDate() - i);
    days?.push(d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
  }
  return days;
}
